import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

const PDF_HEADER = "%PDF";
const PDF_TRAILER = "%%EOF";
const OBJECT_URL_LIFETIME_MS = 60_000;

export interface PdfExportInput {
  readonly bytes: Uint8Array;
  readonly fileName: string;
  readonly title: string;
}

interface NativeFileInput {
  readonly fileName: string;
  readonly base64: string;
}

interface SharedFileInput {
  readonly fileUri: string;
  readonly title: string;
}

interface BrowserDownloadInput {
  readonly bytes: Uint8Array;
  readonly fileName: string;
}

export interface PdfExportRuntime {
  readonly isNativePlatform: () => boolean;
  readonly writeNativeFile: (input: NativeFileInput) => Promise<string>;
  readonly shareNativeFile: (input: SharedFileInput) => Promise<void>;
  readonly deleteNativeFile: (fileName: string) => Promise<void>;
  readonly downloadBrowserFile: (input: BrowserDownloadInput) => void;
}

function decodedSlice(bytes: Uint8Array, start: number, end?: number): string {
  return new TextDecoder("latin1").decode(bytes.slice(start, end));
}

export function assertValidPdfBytes(bytes: Uint8Array): void {
  if (bytes.byteLength < 100 || !decodedSlice(bytes, 0, 4).startsWith(PDF_HEADER)) {
    throw new Error("PDF_INVALID_HEADER");
  }
  if (!decodedSlice(bytes, Math.max(0, bytes.byteLength - 32)).includes(PDF_TRAILER)) {
    throw new Error("PDF_INVALID_TRAILER");
  }
}

export function pdfFileName(value: string): string {
  const withoutExtension = value.replace(/\.pdf$/i, "");
  const normalized = withoutExtension
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96);
  return `${normalized || "samklang-underlag"}.pdf`;
}

export function bytesToBase64(bytes: Uint8Array): string {
  const chunkSize = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, Math.min(offset + chunkSize, bytes.length));
    binary += String.fromCharCode(...chunk);
  }
  return globalThis.btoa(binary);
}

function browserDownload({ bytes, fileName }: BrowserDownloadInput): void {
  const blob = new Blob([bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer], {
    type: "application/pdf",
  });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = globalThis.document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = "noopener";
  globalThis.document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_LIFETIME_MS);
}

const defaultRuntime: PdfExportRuntime = {
  isNativePlatform: () => Capacitor.isNativePlatform(),
  writeNativeFile: async ({ fileName, base64 }) => {
    const result = await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Cache,
      recursive: true,
    });
    return result.uri;
  },
  shareNativeFile: async ({ fileUri, title }) => {
    await Share.share({
      title,
      files: [fileUri],
      dialogTitle: "Spara eller dela PDF",
    });
  },
  deleteNativeFile: async (fileName) => {
    await Filesystem.deleteFile({ path: fileName, directory: Directory.Cache });
  },
  downloadBrowserFile: browserDownload,
};

/**
 * Exports a generated PDF with native Files/Share support in the installed app
 * and a regular download in browsers. The native cache copy is removed after
 * the share sheet closes so participant content is not retained by Samklang.
 */
export async function exportPdf(input: PdfExportInput, runtime: PdfExportRuntime = defaultRuntime): Promise<void> {
  assertValidPdfBytes(input.bytes);
  const fileName = pdfFileName(input.fileName);

  if (!runtime.isNativePlatform()) {
    runtime.downloadBrowserFile({ bytes: input.bytes, fileName });
    return;
  }

  let fileWritten = false;
  try {
    const fileUri = await runtime.writeNativeFile({ fileName, base64: bytesToBase64(input.bytes) });
    fileWritten = true;
    await runtime.shareNativeFile({ fileUri, title: input.title });
  } finally {
    if (fileWritten) await runtime.deleteNativeFile(fileName).catch(() => undefined);
  }
}
